import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, BookOpen, Copy, Eye, EyeOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { CATEGORY_LABELS } from "@/pages/academy/Academy";

export default function AdminCourses() {
  const { t, tt } = useLang();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/courses/${id}`);
      setCourses((cs) => cs.filter((c) => c.id !== id));
      toast.success(t("Kurs gel\u00f6scht", "Course deleted"));
    } catch {
      toast.error(t("Fehler beim L\u00f6schen", "Error deleting"));
    }
  };

  const duplicate = async (id) => {
    try {
      const res = await api.post(`/admin/courses/${id}/duplicate`);
      toast.success(t("Kurs dupliziert (als Entwurf)", "Course duplicated (as draft)"));
      navigate(`/admin/kurse/${res.data.id}`);
    } catch {
      toast.error(t("Fehler beim Duplizieren", "Error duplicating"));
    }
  };

  const togglePublish = async (c) => {
    try {
      const payload = { ...c, published: !c.published };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      await api.patch(`/admin/courses/${c.id}`, payload);
      toast.success(!c.published ? t("Kurs ver\u00f6ffentlicht", "Course published") : t("Kurs auf Entwurf gesetzt", "Course set to draft"));
      load();
    } catch {
      toast.error(t("Fehler beim Speichern", "Error saving"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-courses-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Kursverwaltung", "Course Management")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Eleven Academy \u2013 Kurse, Module, Lektionen und Wissenstests pflegen", "Eleven Academy \u2013 maintain courses, modules, lessons and quizzes")}</p>
        </div>
        <Button className="rounded-[10px] bg-[#0B1220]" onClick={() => navigate("/admin/kurse/neu")} data-testid="course-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> {t("Neuer Kurs", "New course")}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 rounded-[16px]" />)}</div>
      ) : courses.length === 0 ? (
        <Card className="rounded-[16px]"><CardContent className="p-10 text-center text-sm text-muted-foreground">{t("Noch keine Kurse. Erstellen Sie den ersten Kurs.", "No courses yet. Create the first course.")}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {courses.map((c) => (
            <Card key={c.id} className={`rounded-[16px] ${c.archived ? "opacity-60" : ""}`} data-testid={`admin-course-${c.id}`}>
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-10 w-10 rounded-[12px] bg-[#0B1220] flex items-center justify-center shrink-0">
                    <BookOpen className="h-5 w-5 text-[#D6C08D]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{tt(c.title)}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="rounded-full text-[10px]">{tt(CATEGORY_LABELS[c.category] || { de: c.category })}</Badge>
                      {c.is_mandatory && <Badge variant="outline" className="rounded-full text-[10px] border-[#D6C08D]/50 text-[#A8905A]">{t("Pflicht", "Mandatory")}</Badge>}
                      {!c.published && <Badge variant="outline" className="rounded-full text-[10px]">{t("Entwurf", "Draft")}</Badge>}
                      {c.archived && <Badge variant="outline" className="rounded-full text-[10px]">{t("Archiviert", "Archived")}</Badge>}
                      {(c.assigned_roles?.length > 0 || c.assigned_users?.length > 0) && (
                        <Badge variant="outline" className="rounded-full text-[10px]">{t("Zugewiesen", "Assigned")}</Badge>
                      )}
                      <span className="text-[11px] text-muted-foreground">
                        {(c.modules || []).reduce((n, m) => n + (m.lessons || []).length, 0)} {t("Lektionen", "lessons")} {"\u00b7"} {(c.quiz?.questions || []).length} {t("Quizfragen", "quiz questions")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="rounded-[10px]" onClick={() => togglePublish(c)} title={c.published ? t("Auf Entwurf setzen", "Set to draft") : t("Ver\u00f6ffentlichen", "Publish")} data-testid={`course-publish-${c.id}`}>
                    {c.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-[10px]" onClick={() => duplicate(c.id)} title={t("Duplizieren", "Duplicate")} data-testid={`course-duplicate-${c.id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-[10px]" onClick={() => navigate(`/admin/kurse/${c.id}`)} data-testid={`course-edit-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("Bearbeiten", "Edit")}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-[10px] text-destructive hover:text-destructive" data-testid={`course-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("Kurs l\u00f6schen?", "Delete course?")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("Diese Aktion kann nicht r\u00fcckg\u00e4ngig gemacht werden. Tipp: Archivieren statt l\u00f6schen erh\u00e4lt Abschl\u00fcsse.", "This action cannot be undone. Tip: archiving instead of deleting preserves completions.")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-[10px]">{t("Abbrechen", "Cancel")}</AlertDialogCancel>
                        <AlertDialogAction className="rounded-[10px] bg-destructive hover:bg-destructive/90" onClick={() => remove(c.id)} data-testid={`course-delete-confirm-${c.id}`}>
                          {t("L\u00f6schen", "Delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
